import React from 'react';

/**
 * Enhanced PageHeader Component
 * 
 * @param {string} title - Main page title
 * @param {string} subtitle - Optional subtitle (shown below title)
 * @param {string} description - Optional description text
 * @param {Array} actions - Array of action button objects
 * 
 * Action button object structure:
 * {
 *   label: string,           // Button text (required)
 *   icon: ReactNode,         // Optional icon component
 *   onClick: function,       // Click handler (required)
 *   variant: string,         // 'primary' | 'outline' | 'secondary' (default: 'outline')
 *   disabled: boolean,       // Optional disabled state
 *   className: string,       // Optional additional CSS classes
 *   style: object,           // Optional inline styles
 *   tooltip: string          // Optional tooltip text
 * }
 */
const PageHeader = ({ 
  title,
  subtitle = null,
  description = null,
  actions = [],
  customActionElement = null
}) => {
  const getButtonClasses = (variant = 'outline') => {
    const baseClasses = 'btn-modern d-flex align-items-center';
    const variantClasses = {
      'primary': 'btn-modern-primary',
      'outline': 'btn-modern-outline',
      'secondary': 'btn-modern-outline'
    };
    return `${baseClasses} ${variantClasses[variant] || variantClasses.outline}`;
  };

  return (
    <>
      {/* Responsive CSS for PageHeader */}
      <style>{`
        .page-header-container {
          position: relative;
        }
        
        .page-header-content-wrapper {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: flex-start;
          gap: 24px;
          flex-wrap: wrap;
        }
        
        .page-header-title-section {
          flex: 1 1 auto;
          min-width: 200px;
        }
        
        .page-header-actions {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 0;
          margin-left: auto;
          flex-shrink: 0;
          padding-right: 32px; /* Align with table below (table is 48px from edge, header is 16px, so add 32px) */
        }
        
        /* Responsive: On smaller screens, buttons move below title */
        @media (max-width: 768px) {
          .page-header-content-wrapper {
            flex-direction: column;
          }
          
          .page-header-actions {
            width: 100%;
            margin-top: 16px;
            margin-left: 0;
            justify-content: flex-end;
          }
        }
        
        /* On very small screens, stack buttons vertically */
        @media (max-width: 480px) {
          .page-header-actions {
            flex-direction: column;
            width: 100%;
          }
          
          .page-header-actions button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
      
      <div 
        className="page-header-container"
        style={{
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          padding: '22px 16px',
          margin: 0,
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          fontFamily: 'var(--font-family)',
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Header Content - Flex container for title and actions */}
        <div className="page-header-content-wrapper">
          {/* Left Side - Title Section */}
          <div className="page-header-title-section">
            {/* Page Title */}
            {title && (
              <h1 
                className="page-header-title"
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1F2937',
                  margin: 0,
                  marginBottom: subtitle || description ? '8px' : '0',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.4'
                }}
              >
                {title}
              </h1>
            )}

            {/* Page Subtitle */}
            {subtitle && (
              <p 
                className="page-header-subtitle"
                style={{
                  fontSize: '15px',
                  fontWeight: '500',
                  color: '#6B7280',
                  margin: 0,
                  marginBottom: description ? '6px' : '0',
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.5'
                }}
              >
                {subtitle}
              </p>
            )}

            {/* Page Description */}
            {description && (
              <p 
                className="page-header-description"
                style={{
                  fontSize: '14px',
                  fontWeight: '400',
                  color: '#6B7280',
                  margin: 0,
                  fontFamily: 'var(--font-family)',
                  lineHeight: '1.6'
                }}
              >
                {description}
              </p>
            )}
          </div>

          {/* Right Side - Action Buttons */}
          {((actions && actions.length > 0) || customActionElement) && (
            <div className="page-header-actions">
              {customActionElement && (
                <div className="custom-action-container">
                  {customActionElement}
                </div>
              )}
              {actions && actions.map((action, index) => {
                if (!action) return null;
                
                // Check if it's an icon-only button (no label or empty label)
                const isIconOnly = !action.label || action.label.trim() === '';
                
                return (
                  <button
                    key={index}
                    onClick={action.onClick}
                    disabled={action.disabled || false}
                    className={`${getButtonClasses(action.variant)} ${action.className || ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isIconOnly ? '0' : '8px',
                      whiteSpace: 'nowrap',
                      ...(isIconOnly ? {
                        width: '44px',
                        height: '44px',
                        padding: '0',
                        minWidth: '44px'
                      } : {}),
                      ...(action.style || {})
                    }}
                    title={action.tooltip || action.label || 'Action'}
                  >
                    {action.icon && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0, flexShrink: 0 }}>
                        {action.icon}
                      </span>
                    )}
                    {!isIconOnly && <span>{action.label}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PageHeader;
